/// <reference lib="dom" />

import { memo, useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { Props } from '.';
import { TimerType } from '../../types/others';

const isVidaa = (window as any).idcPlatform === 'vidaa';

const holdDelay = 500;

const KeyWrapper = memo<Props>(
  ({
    enabled,
    up,
    down,
    left,
    right,
    confirm,
    confirmHold,
    cancel,
    digit,
    custom,
    backspace,
    scrollOverride,
    ...props
  }) => {
    const ref = useRef<View>(null);
    const confirmHoldTimer = useRef<TimerType>(undefined);
    const preventConfirmHold = useRef(false);

    const keyHandler = useCallback(
      (e: KeyboardEvent) => {
        const d = +e.key;
        if (e.key !== ' ' && !Number.isNaN(d)) {
          digit?.(d);
          return;
        }

        if (confirmKeys.includes(e.which) && !e.repeat) {
          if (confirmHold) {
            preventConfirmHold.current = false;
            confirmHoldTimer.current = setTimeout(() => {
              preventConfirmHold.current = true;
              confirmHold?.();
            }, holdDelay);
          } else confirm?.();
          return;
        }

        switch (e.which) {
          case 38:
            up?.();
            return;
          case 40:
            down?.();
            return;
          case 37:
            left?.();
            return;
          case 39:
            right?.();
            return;
          case 10190: //prev channel (tizen)
            custom?.('PrevChannel');
            break;
          case 10073: //channel list
            custom?.('ChannelList');
            break;
          case 33: //page up
          case 427: //channel up (tizen)
            custom?.('ChannelUp');
            break;
          case 34: //page down
          case 428: //channel down (tizen)
            custom?.('ChannelDown');
            break;
          case 10252: //play/pause (tizen)
            custom?.('PlayPause');
            break;
          case 415: //play
            custom?.('Play');
            break;
          case 19: //pause
          case 413: //stop
            custom?.('Pause');
            break;
          case 412: //rewind
          case 40232: //prev track (tizen)
            custom?.('Rewind');
            break;
          case 417: //fast forward
          case 40233: //next track (tizen)
            custom?.('FForward');
            break;
        }

        if (cancelKeys.includes(e.which)) cancel?.();
        else if (!isVidaa && e.which === 8) backspace?.();
      },
      [up, down, left, right, confirm, confirmHold, cancel],
    );
    const keyUpHandler = useCallback(
      (e: KeyboardEvent) => {
        if (confirmKeys.includes(e.which)) {
          if (
            confirmHold === undefined ||
            confirmHoldTimer.current === undefined
          )
            return;

          clearTimeout(confirmHoldTimer.current);
          confirmHoldTimer.current = undefined;
          if (!preventConfirmHold.current) confirm?.();
          return;
        }

        switch (e.which) {
          case 38:
            up?.(true);
            return;
          case 40:
            down?.(true);
            return;
          case 37:
            left?.(true);
            return;
          case 39:
            right?.(true);
            return;
        }
      },
      [confirmHold],
    );

    useEffect(() => {
      const e = enabled ?? true;
      if (e) {
        const options = { capture: true };
        document.addEventListener('keydown', keyHandler, options);
        document.addEventListener('keyup', keyUpHandler, options);
        return () => {
          document.removeEventListener('keydown', keyHandler, options);
          document.removeEventListener('keyup', keyUpHandler, options);
        };
      }
      return;
    }, [enabled, keyHandler, keyUpHandler]);

    if ((window as any).idcPlatform === 'web') {
      useEffect(() => {
        const cb = scrollOverride
          ? (e: WheelEvent) => {
              e.preventDefault();
              scrollOverride?.();
            }
          : (e: WheelEvent) => {
              e.preventDefault();
              if (e.deltaY < 0) up?.();
              else if (e.deltaY > 0) down?.();
              else if (e.deltaX > 0) right?.();
              else if (e.deltaX < 0) left?.();
            };

        (ref.current as HTMLDivElement | null)?.addEventListener('wheel', cb);
        return () =>
          (ref.current as HTMLDivElement | null)?.removeEventListener(
            'wheel',
            cb,
          );
      }, [scrollOverride, up, down, left, right]);
    }

    return <View {...props} ref={ref} />;
  },
);
KeyWrapper.displayName = 'KeyWrapperTv';

const confirmKeys = [13, 32];
const cancelKeys = isVidaa ? [8] : [27, 461, 10009];

export default KeyWrapper;
